/**
 * New local (unrotated) width/height for a corner-drag on a rotated box.
 *
 * interact.js only understands screen/axis-aligned space, so once a box is
 * rotated its resize is wrong (near ±45° a corner drag collapses to a single
 * AABB edge and width bleeds into height). We instead project the pointer's
 * screen delta onto the box's own local axes so width and height stay
 * decoupled — narrowing width never changes height.
 *
 * @param rotationDeg  box rotation, degrees
 * @param corner       grabbed handle: corner "nw" | "ne" | "sw" | "se", or a
 *                     single edge like "s" (only that axis resizes)
 * @param dxScreen     pointer delta X since gesture start, screen px
 * @param dyScreen     pointer delta Y since gesture start, screen px
 * @param startW/startH  local size at gesture start, local (unscaled) px
 * @param scale        canvas zoom (screen px / local px)
 * @param aspect       keep width:height ratio (images)
 * @param minW/minH    minimum local size
 * @returns {{newW:number,newH:number}}
 */
export function computeRotatedResize({
  rotationDeg,
  corner,
  dxScreen,
  dyScreen,
  startW,
  startH,
  scale = 1,
  aspect = false,
  minW = 1,
  minH = 1,
}) {
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  // Pointer delta projected onto the box's local X/Y axes → local px.
  const localDX = (dxScreen * cos + dyScreen * sin) / scale;
  const localDY = (-dxScreen * sin + dyScreen * cos) / scale;
  // east grows width with +localDX, south grows height with +localDY;
  // 0 = axis not driven by this handle (single-edge grips like "s").
  const cx = corner.indexOf("e") !== -1 ? 1 : corner.indexOf("w") !== -1 ? -1 : 0;
  const cy = corner.indexOf("s") !== -1 ? 1 : corner.indexOf("n") !== -1 ? -1 : 0;
  let newW = Math.max(startW + cx * localDX, minW);
  let newH = Math.max(startH + cy * localDY, minH);
  if (aspect && startW > 0 && startH > 0) {
    // Uniform scale driven by whichever axis moved more (relative).
    const fW = newW / startW;
    const fH = newH / startH;
    const f = Math.abs(fW - 1) >= Math.abs(fH - 1) ? fW : fH;
    newW = Math.max(startW * f, minW);
    newH = Math.max(startH * f, minH);
  }
  return { newW, newH };
}
